"use client"

import Tracker, { Messages } from "@openreplay/tracker"
import { usePathname } from "next/navigation"
import { useEffect } from "react"

import { isOpenReplayRouteEligible } from "./openreplay-eligibility"

type OpenReplayConfig = {
  enabled: boolean
  project_key: string | null
}

const INGEST_POINT = "https://replay.vetpets.com.tr/ingest"
const ENTRY_URL_METADATA_KEY = "vetpets_entry_url"
const ENTRY_REFERRER_METADATA_KEY = "vetpets_entry_referrer"
const SENSITIVE_QUERY_KEYS = new Set([
  "jwt",
  "password",
  "reset-password",
  "invitation",
  "secret",
  "token",
])

const sanitizeReplayUrl = (value: string): string => {
  if (!value) return ""

  try {
    const parsed = new URL(value)
    parsed.searchParams.forEach((paramValue, key) => {
      if (SENSITIVE_QUERY_KEYS.has(key.toLowerCase())) {
        parsed.searchParams.set(key, "*".repeat(paramValue.length))
      }
    })
    return parsed.toString()
  } catch {
    return ""
  }
}

type TrackerOptions = ConstructorParameters<typeof Tracker>[0]
const PRIVACY_SAFE_NETWORK_OPTIONS = {
  capturePayload: false,
  ignoreHeaders: true,
  captureInIframes: false,
} as NonNullable<TrackerOptions["network"]>

let tracker: Tracker | null = null
let configuredProjectKey: string | null = null
let startPending = false
let startGeneration = 0
let recordingDesired = false

const normalizedKey = (value: string | null): string => {
  if (typeof value !== "string") return ""
  const trimmed = value.trim()
  return trimmed.length >= 1 && trimmed.length <= 512 ? trimmed : ""
}

const stopRecording = () => {
  recordingDesired = false
  startGeneration += 1
  startPending = false
  if (!tracker) return

  try {
    tracker.stop()
  } catch {
    // Optional telemetry must never affect commerce.
  }
}

const startRecording = () => {
  recordingDesired = true
  if (!tracker || startPending) return

  try {
    if (tracker.isActive()) return
  } catch {
    // Continue to one guarded start attempt.
  }

  const current = tracker
  const generation = ++startGeneration
  startPending = true

  try {
    const entryURL = sanitizeReplayUrl(window.location.href)
    const entryReferrer = sanitizeReplayUrl(document.referrer)
    const started = current.start({
      metadata: {
        [ENTRY_URL_METADATA_KEY]: entryURL,
        [ENTRY_REFERRER_METADATA_KEY]: entryReferrer,
      },
    })
    void Promise.resolve(started)
      .then(() => {
        if (generation === startGeneration && recordingDesired) {
          try {
            if (current.isActive()) {
              current.use((app) => {
                if (!app) return
                app.send(Messages.Metadata(ENTRY_URL_METADATA_KEY, entryURL))
                app.send(
                  Messages.Metadata(ENTRY_REFERRER_METADATA_KEY, entryReferrer),
                  true
                )
              })
              current.forceFlushBatch()
            }
          } catch {
            // Optional telemetry must never affect commerce.
          }
          return
        }

        if (generation !== startGeneration && !recordingDesired) {
          try {
            current.stop()
          } catch {
            // Route changed while start was pending.
          }
        }
      })
      .catch(() => undefined)
      .finally(() => {
        if (generation === startGeneration) startPending = false
      })
  } catch {
    if (generation === startGeneration) startPending = false
  }
}

export default function OpenReplayRuntime({
  config,
}: {
  config: OpenReplayConfig
}) {
  const pathname = usePathname() || "/"

  useEffect(() => {
    const projectKey = normalizedKey(config.project_key)

    if (
      !config.enabled ||
      !projectKey ||
      !isOpenReplayRouteEligible(pathname)
    ) {
      stopRecording()
      return
    }

    if (configuredProjectKey && configuredProjectKey !== projectKey) {
      return
    }

    if (!tracker) {
      try {
        tracker = new Tracker({
          projectKey,
          ingestPoint: INGEST_POINT,
          defaultInputMode: 2,
          consoleMethods: null,
          captureExceptions: false,
          // Tracker 18.1.6 runtime accepts Partial<NetworkOptions>, while its
          // exported constructor type intersects that with the full network shape.
          network: PRIVACY_SAFE_NETWORK_OPTIONS,
        })
        configuredProjectKey = projectKey
      } catch {
        tracker = null
        configuredProjectKey = null
        return
      }
    }

    startRecording()
  }, [config.enabled, config.project_key, pathname])

  return null
}
